import { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';

interface ParteFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  processoId: string;
  processoTitulo: string;
  tipoProcesso: 'venda' | 'locacao' | 'prestacao_servico';
  parte?: {
    id: string;
    tipo_parte: string;
    nome_completo: string;
    email?: string | null;
  } | null;
}

const ParteForm = ({ isOpen, onClose, onSave, processoId, processoTitulo, tipoProcesso, parte }: ParteFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tipo_parte: '',
    nome_completo: '',
    email: '',
    deve_receber_contrato: true
  });

  // Reset form quando modal abre ou quando parte muda
  useEffect(() => {
    if (isOpen) {
      if (parte) {
        // Modo edição
        setFormData({
          tipo_parte: parte.tipo_parte,
          nome_completo: parte.nome_completo,
          email: parte.email || '',
          deve_receber_contrato: true
        });
      } else {
        // Modo criação
        setFormData({
          tipo_parte: '',
          nome_completo: '',
          email: '',
          deve_receber_contrato: true
        });
      }
    }
  }, [isOpen, parte]);

  const tiposParteVenda = [
    { value: 'comprador', label: 'Comprador' },
    { value: 'vendedor', label: 'Vendedor' }
  ];

  const tiposParteLocacao = [
    { value: 'locatario', label: 'Locatário' },
    { value: 'locador', label: 'Locador' },
    { value: 'fiador', label: 'Fiador' }
  ];

  const tiposPartePrestacaoServico = [
    { value: 'contratante', label: 'Contratante' },
    { value: 'contratado', label: 'Contratado/Prestador' }
  ];

  const tiposParte = tipoProcesso === 'venda' 
    ? tiposParteVenda 
    : tipoProcesso === 'locacao' 
    ? tiposParteLocacao 
    : tiposPartePrestacaoServico;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.tipo_parte || !formData.email) {
      toast({
        title: "Erro",
        description: "Preencha o tipo de parte e o email",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (parte) {
        // Modo edição - atualizar parte existente
        const { error: updateError } = await supabase
          .from('partes_processo')
          .update({
            tipo_parte: formData.tipo_parte,
            nome_completo: formData.nome_completo || parte.nome_completo,
            email: formData.email,
            deve_receber_contrato: formData.deve_receber_contrato
          })
          .eq('id', parte.id);

        if (updateError) throw updateError;

        toast({
          title: "Sucesso",
          description: "Dados da parte atualizados com sucesso!",
        });

        onSave();
        return;
      }

      // Modo criação - gerar token e enviar link (a parte será criada quando o formulário público for enviado)
      const token = crypto.randomUUID();
      
      // Obter ID do usuário atual para auditoria
      const { data: { user } } = await supabase.auth.getUser();
      
      console.log('Criando token para formulário público:', {
        processo_id: processoId,
        tipo_parte: formData.tipo_parte,
        created_by: user?.id
      });
      
      // Inserir apenas o token (sem parte_id, que será preenchido pelo formulário público)
      const { error: tokenError } = await supabase
        .from('tokens_formulario_publico')
        .insert({
          processo_id: processoId,
          token,
          tipo_parte: formData.tipo_parte,
          status: 'pendente',
          created_by: user?.id
        });

      if (tokenError) {
        console.error('Erro detalhado ao criar token:', {
          error: tokenError,
          code: tokenError.code,
          message: tokenError.message,
          details: tokenError.details,
          hint: tokenError.hint
        });
        throw tokenError;
      }

      // Enviar email com link para formulário
      const { error: emailError } = await supabase.functions.invoke('send-form-link-email', {
        body: {
          destinatario_email: formData.email,
          destinatario_nome: formData.nome_completo || '',
          link_formulario: `${window.location.origin}/formulario/${token}`,
          processo_titulo: processoTitulo,
          tipo_parte: tiposParte.find(t => t.value === formData.tipo_parte)?.label || formData.tipo_parte,
          validade: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR')
        }
      });

      if (emailError) {
        console.error('Erro ao enviar email:', emailError);
        toast({
          title: "Atenção",
          description: "Token gerado, mas houve erro ao enviar o email. Copie o link manualmente.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Link enviado!",
          description: `Email enviado para ${formData.email} com o link do formulário`,
        });
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Erro ao gerar link:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao gerar link para parte",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={onClose} />
          <div className="relative bg-background border border-border rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                {parte ? 'Editar Parte' : 'Gerar Link para Parte'}
              </h2>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    📧 Um email será enviado com o link para a parte preencher seus próprios dados
                  </p>
                </div>

                {/* Tipo de Parte */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Parte <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.tipo_parte}
                    onChange={(e) => setFormData({ ...formData, tipo_parte: e.target.value })}
                    required
                  >
                    <option value="">Selecione...</option>
                    {tiposParte.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Nome (opcional) */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Nome <span className="text-muted-foreground text-xs">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.nome_completo}
                    onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                    placeholder="Para referência no sistema"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>

                {/* Deve Receber Contrato */}
                <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                  <input
                    type="checkbox"
                    id="deve_receber_contrato"
                    checked={formData.deve_receber_contrato}
                    onChange={(e) => setFormData({ ...formData, deve_receber_contrato: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="deve_receber_contrato" className="text-sm font-medium cursor-pointer">
                    Deve receber contrato para revisão
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Gerar e Enviar Link
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ParteForm;
